import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Card, CardContent, CardHeader, CardTitle, ConfirmDialog, Input } from '@mlm/ui';
import {
  categoryImageUrl,
  createCategory,
  deleteCategory,
  listCategories,
  updateCategory,
  uploadCategoryAsset,
  type Category,
} from '../lib/api';

/**
 * Categories CMS. Product categories power the storefront category grid and the
 * product filter. Flat list with optional parent for sub-categories. `code` is
 * an immutable uppercase slug; `name` + `sortOrder` are editable.
 */
export function CategoriesPage() {
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: ['admin-categories'], queryFn: listCategories });
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<Category | null>(null);

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: ['admin-categories'] });
    queryClient.invalidateQueries({ queryKey: ['categories'] });
  }

  const create = useMutation({
    mutationFn: createCategory,
    meta: { successMessage: 'Category created.' },
    onSuccess: () => {
      invalidate();
      setCreating(false);
    },
  });
  const update = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<Category> }) =>
      updateCategory(id, patch),
    meta: { successMessage: 'Category updated.' },
    onSuccess: () => {
      invalidate();
      setEditing(null);
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) => deleteCategory(id),
    meta: { successMessage: 'Category deleted.' },
    onSuccess: () => {
      invalidate();
      setDeleting(null);
    },
  });

  const categories = query.data ?? [];
  const nameById = new Map(categories.map((c) => [c.id, c.name]));

  return (
    <div className="space-y-4">
      <div className="flex items-baseline justify-between">
        <h1 className="text-2xl font-semibold">Categories</h1>
        <Button onClick={() => setCreating(true)}>+ New category</Button>
      </div>
      <p className="text-sm text-muted-foreground">
        Categories appear in the storefront category grid and product filters. Deleting a category
        leaves its products uncategorized and promotes any sub-categories to top level.
      </p>

      {creating && (
        <CategoryForm
          categories={categories}
          submitting={create.isPending}
          onSubmit={(input) => create.mutate(input)}
          onCancel={() => setCreating(false)}
          error={create.error}
        />
      )}

      <Card>
        <CardHeader>
          <CardTitle>All categories</CardTitle>
        </CardHeader>
        <CardContent>
          {query.isLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : categories.length === 0 ? (
            <p className="text-sm text-muted-foreground">No categories yet.</p>
          ) : (
            <ul className="divide-y">
              {categories.map((c) => (
                <li key={c.id} className="py-3">
                  {editing === c.id ? (
                    <CategoryForm
                      category={c}
                      categories={categories}
                      submitting={update.isPending}
                      onSubmit={(input) =>
                        update.mutate({
                          id: c.id,
                          patch: {
                            name: input.name,
                            parentId: input.parentId,
                            sortOrder: input.sortOrder,
                            imageKey: input.imageKey,
                          },
                        })
                      }
                      onCancel={() => setEditing(null)}
                      error={update.error}
                    />
                  ) : (
                    <div className="flex items-center justify-between gap-3">
                      {c.imageKey ? (
                        <img
                          src={categoryImageUrl(c.id)}
                          alt=""
                          className="h-10 w-10 shrink-0 rounded-md border object-cover"
                        />
                      ) : (
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md border bg-muted text-[9px] text-muted-foreground">
                          no img
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="truncate font-medium">{c.name}</span>
                          <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs text-muted-foreground">
                            {c.code}
                          </span>
                          {c.parentId && (
                            <span className="rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
                              ↳ {nameById.get(c.parentId) ?? 'parent'}
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground">sort {c.sortOrder}</div>
                      </div>
                      <Button variant="outline" size="sm" onClick={() => setEditing(c.id)}>
                        Edit
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => setDeleting(c)}>
                        Delete
                      </Button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <ConfirmDialog
        open={!!deleting}
        onOpenChange={(o) => !o && setDeleting(null)}
        title={`Delete "${deleting?.name}"?`}
        description="Products in this category become uncategorized and any sub-categories move to top level. This can't be undone."
        confirmLabel="Delete"
        confirmVariant="destructive"
        onConfirm={() => {
          if (deleting) remove.mutate(deleting.id);
        }}
      />
    </div>
  );
}

function CategoryForm({
  category,
  categories,
  submitting,
  onSubmit,
  onCancel,
  error,
}: {
  category?: Category;
  categories: Category[];
  submitting: boolean;
  onSubmit: (input: {
    code: string;
    name: string;
    parentId: string | null;
    sortOrder: number;
    imageKey: string;
  }) => void;
  onCancel: () => void;
  error: unknown;
}) {
  const [code, setCode] = useState(category?.code ?? '');
  const [name, setName] = useState(category?.name ?? '');
  const [parentId, setParentId] = useState<string>(category?.parentId ?? '');
  const [sortOrder, setSortOrder] = useState<string>(String(category?.sortOrder ?? 0));
  const [imageKey, setImageKey] = useState<string>(category?.imageKey ?? '');
  const [localPreview, setLocalPreview] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!imageKey) return;
    onSubmit({
      code: code.toUpperCase(),
      name: name.trim(),
      parentId: parentId || null,
      sortOrder: Number(sortOrder) || 0,
      imageKey,
    });
  }

  async function onPickImage(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setLocalPreview(URL.createObjectURL(file));
    setUploading(true);
    try {
      const { key } = await uploadCategoryAsset(file);
      setImageKey(key);
    } finally {
      setUploading(false);
    }
  }

  const errMsg = (error as { response?: { data?: { error?: string } } } | null)?.response?.data
    ?.error;
  const parentOptions = categories.filter((c) => c.id !== category?.id);
  // Show the freshly-picked file (object URL) while editing; fall back to the
  // saved category's image (the public /categories/:id/image redirect).
  const previewSrc = localPreview ?? (category?.imageKey ? categoryImageUrl(category.id) : null);

  return (
    <div className="space-y-3 rounded-md border bg-muted/30 p-4">
      <h3 className="text-base font-semibold leading-none tracking-tight">
        {category ? 'Edit category' : 'New category'}
      </h3>
      <form onSubmit={submit} className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">Code</label>
          <Input
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            disabled={!!category}
            placeholder="WELLNESS, COFFEE_DRINKS, …"
            required
          />
          <p className="text-[10px] text-muted-foreground">
            Uppercase letters, digits, underscore. Immutable once created.
          </p>
        </div>
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">Name</label>
          <Input value={name} onChange={(e) => setName(e.target.value)} required />
        </div>
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">Parent (optional)</label>
          <select
            value={parentId}
            onChange={(e) => setParentId(e.target.value)}
            className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
          >
            <option value="">— Top level —</option>
            {parentOptions.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">Sort order</label>
          <Input
            type="number"
            step={1}
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
          />
        </div>
        <div className="space-y-1 md:col-span-2">
          <label className="text-xs text-muted-foreground">Tile image (required)</label>
          <div className="flex items-center gap-3">
            {previewSrc ? (
              <img src={previewSrc} alt="" className="h-16 w-16 rounded-md border object-cover" />
            ) : (
              <div className="flex h-16 w-16 items-center justify-center rounded-md border bg-muted text-[10px] text-muted-foreground">
                No image
              </div>
            )}
            <div>
              <input type="file" accept="image/*" onChange={onPickImage} disabled={uploading} />
              {uploading && <p className="text-xs text-muted-foreground">Uploading…</p>}
            </div>
          </div>
          <p className="text-[10px] text-muted-foreground">
            Shown on the storefront &ldquo;Shop by Category&rdquo; grid.
          </p>
        </div>
        {errMsg && <p className="text-sm text-destructive md:col-span-2">{errMsg}</p>}
        <div className="flex gap-2 pt-2 md:col-span-2">
          <Button type="submit" disabled={submitting || uploading || !imageKey}>
            {submitting ? 'Saving…' : category ? 'Save' : 'Create'}
          </Button>
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
}
